import { Navigate, Outlet } from "react-router-dom";
import {
  FolderOpen,
  HandCoins,
  IdCard,
  LayoutDashboard,
  LifeBuoy,
  Receipt,
  ScrollText,
  Store,
  TimerReset,
  UserRound,
  Users,
} from "lucide-react";
import { AppShell, type NavMap } from "@/shared/ui/layout";
import { useAuth } from "@/features/auth/auth.context";
import { navForCountry } from "@/features/kyc/kycNav";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El panel del FRANQUICIATARIO — "se le genera un nuevo panel
 * administrador" (Anahí, 27-ago).
 *
 * Es el MISMO armazón (`AppShell`) del panel de administración con OTRO
 * mapa de navegación: las secciones que ella dictó — dashboard, KYC, su
 * red o invitados, sus órdenes y su tienda. El armazón recibe datos, no
 * roles, así que reutilizarlo no filtra nada del panel de la casa.
 *
 * ── La guarda ────────────────────────────────────────────────────────
 * Sin sesión → al login con regreso. Con sesión pero SIN franquicia
 * validada → a adquirirla: este panel nace con la franquicia, no antes.
 * ═══════════════════════════════════════════════════════════════════
 */
const FRANCHISEE_NAV: NavMap = [
  {
    label: "Mi negocio",
    items: [
      { to: "/mi-negocio", label: "Dashboard", icon: LayoutDashboard },
      { to: "/mi-negocio/tienda", label: "Mi tienda", icon: Store },
      { to: "/mi-negocio/ordenes", label: "Mis órdenes", icon: Receipt },
      { to: "/mi-negocio/reloj", label: "Reloj de recompra", icon: TimerReset },
      { to: "/mi-negocio/red", label: "Mi red", icon: Users },
      { to: "/mi-negocio/comisiones", label: "Mis comisiones", icon: HandCoins },
      { to: "/mi-negocio/recursos", label: "Recursos", icon: FolderOpen },
      { to: "/mi-negocio/kyc", label: "KYC", icon: IdCard },
      { to: "/mi-negocio/perfil", label: "Mi perfil", icon: UserRound },
    ],
  },
  {
    label: "Pantallas públicas",
    note: "Viven fuera del panel y traen su propio cromo.",
    external: true,
    /*
      Soporte y Legal, que el socio SÍ tenía y el franquiciatario no
      (Anahí, 29-ago, preguntando dónde se ven los documentos legales).
      Los documentos que se editan en /admin/legal-docs se leen en
      /legal; sin este enlace, quien dirige una franquicia tenía que
      salir a la tienda y bajar hasta el pie para encontrarlos.
    */
    items: [
      { to: "/shop", label: "Tienda It's The Best", icon: Store },
      { to: "/soporte", label: "Soporte", icon: LifeBuoy },
      { to: "/legal", label: "Legal", icon: ScrollText },
    ],
  },
];

export function FranchiseeShell() {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: "/mi-negocio" }} />;
  }
  /*
   * El super-admin PASA sin franquicia (Anahí, 27-ago): el conmutador
   * de vistas existe para revisar cómo se ve cada panel. Las pantallas
   * enseñan sus estados vacíos honestos — ver no es tener.
   */
  if (!user?.franchiseeSince && user?.role !== "super_admin") {
    return <Navigate to="/franquicias/adquirir" replace />;
  }

  return (
    <AppShell nav={navForCountry(FRANCHISEE_NAV, user?.country)}>
      <Outlet />
    </AppShell>
  );
}
